// Brand Color Palette for Charts
// This palette provides a comprehensive set of colors for displaying charts
// Each color is carefully selected for optimal visibility and accessibility

export const CHART_COLORS = [
  "#7F56D9", // Deep Purple (Brand Primary)
  "#2E90FA", // Bright Blue (Brand Secondary)
  "#9B8AFB", // Medium Purple
  "#7CD4FD", // Light Blue/Cyan
  "#D6BBFB", // Soft Purple/Lavender
  "#84CAFF", // Soft Sky Blue
  "#6172F3", // Indigo
  "#B2DDFF", // Very Light Blue
  "#E9D7FE", // Extra Light Purple
  "#1570EF", // Rich Blue
  "#A4BCFD", // Dusty Blue
  "#9E77ED", // Royal Purple
  "#D1E9FF", // Pale Blue
  "#F4EBFF", // Pale Purple
  "#B9E6FE", // Powder Blue
  "#CECFEF", // Periwinkle
];

/** Higher-contrast palette for dark mode (avoid pale pastels on dark bg). */
export const CHART_COLORS_DARK = [
  "#A78BFA",
  "#38BDF8",
  "#818CF8",
  "#2DD4BF",
  "#F472B6",
  "#60A5FA",
  "#FBBF24",
  "#34D399",
  "#C084FC",
  "#22D3EE",
  "#FB7185",
  "#A3E635",
  "#E879F9",
  "#67E8F9",
  "#FCD34D",
  "#93C5FD",
];

function getActivePalette() {
  const isDark =
    typeof document !== "undefined" &&
    document.documentElement.classList.contains("dark");
  return isDark ? CHART_COLORS_DARK : CHART_COLORS;
}

/**
 * Get colors for charts based on the number of data points needed
 * @param {number} count - Number of colors needed
 * @param {string} brandColor - Optional brand color to use as primary
 * @returns {Array} Array of color hex codes
 */
export const getChartColors = (count, brandColor = null) => {
  if (!count || count <= 0) return [];
  
  // If brand color is provided, use it as the first color and generate variations
  if (brandColor) {
    return generateBrandColorVariations(count, brandColor);
  }
  
  const palette = getActivePalette();
  const colors = [];
  for (let i = 0; i < count; i++) {
    colors.push(palette[i % palette.length]);
  }
  
  return colors;
};

/**
 * Generate color variations based on a brand color
 * @param {number} count - Number of colors needed
 * @param {string} brandColor - Brand color hex code
 * @returns {Array} Array of color hex codes
 */
export const generateBrandColorVariations = (count, brandColor) => {
  if (!brandColor) return getChartColors(count);
  
  const colors = [brandColor];
  
  // Extract RGB values from brand color
  const hex = brandColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Generate variations by adjusting brightness and hue
  const palette = getActivePalette();
  for (let i = 1; i < count; i++) {
    // Use predefined colors for variety, but ensure brand color is primary
    if (i < palette.length) {
      colors.push(palette[i]);
    } else {
      // Generate a variation of the brand color
      const factor = 0.7 + (i * 0.1);
      const newR = Math.round(Math.min(255, r * factor));
      const newG = Math.round(Math.min(255, g * factor));
      const newB = Math.round(Math.min(255, b * factor));
      
      const toHex = (n) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
      colors.push(`#${toHex(newR)}${toHex(newG)}${toHex(newB)}`);
    }
  }
  
  return colors;
};

/**
 * Get colors with alpha transparency for charts that need transparency
 * @param {number} count - Number of colors needed
 * @param {string} brandColor - Optional brand color
 * @param {number} alpha - Alpha value (0-1)
 * @returns {Array} Array of color hex codes with alpha
 */
export const getChartColorsWithAlpha = (count, brandColor = null, alpha = 0.8) => {
  const baseColors = getChartColors(count, brandColor);
  
  return baseColors.map(color => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const a = Math.round(255 * alpha);
    
    const toHex = (n) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
    return `#${toHex(r)}${toHex(g)}${toHex(b)}${toHex(a)}`;
  });
};

/**
 * Get a single color from the palette by index
 * @param {number} index - Index of the color to get
 * @returns {string} Color hex code
 */
export const getChartColor = (index) => {
  const palette = getActivePalette();
  return palette[index % palette.length];
};

/**
 * Get a gradient of colors for line charts or area charts
 * @param {number} count - Number of gradient stops
 * @param {string} brandColor - Optional brand color
 * @returns {Array} Array of color hex codes for gradient
 */
export const getChartGradientColors = (count, brandColor = null) => {
  if (brandColor) {
    // Create a gradient from brand color to a complementary color
    const colors = [brandColor];
    const complementaryColor = getComplementaryColor(brandColor);
    
    for (let i = 1; i < count; i++) {
      const ratio = i / (count - 1);
      const color = interpolateColors(brandColor, complementaryColor, ratio);
      colors.push(color);
    }
    
    return colors;
  }
  
  // Use predefined gradient colors
  return getActivePalette().slice(0, count);
};

/**
 * Get complementary color for a given color
 * @param {string} color - Hex color code
 * @returns {string} Complementary hex color code
 */
export const getComplementaryColor = (color) => {
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const toHex = (n) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
  return `#${toHex(255 - r)}${toHex(255 - g)}${toHex(255 - b)}`;
};

/**
 * Interpolate between two colors
 * @param {string} color1 - First hex color code
 * @param {string} color2 - Second hex color code
 * @param {number} ratio - Interpolation ratio (0-1)
 * @returns {string} Interpolated hex color code
 */
export const interpolateColors = (color1, color2, ratio) => {
  const hex1 = color1.replace('#', '');
  const hex2 = color2.replace('#', '');
  
  const r1 = parseInt(hex1.substr(0, 2), 16);
  const g1 = parseInt(hex1.substr(2, 2), 16);
  const b1 = parseInt(hex1.substr(4, 2), 16);
  
  const r2 = parseInt(hex2.substr(0, 2), 16);
  const g2 = parseInt(hex2.substr(2, 2), 16);
  const b2 = parseInt(hex2.substr(4, 2), 16);
  
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);
  
  const toHex = (n) => Math.min(255, Math.max(0, n)).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}; 